import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { TrendingUp, GraduationCap, Heart, Users, Briefcase, Shield, BookOpen, Activity } from "lucide-react";

function Intervene() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const interventions = [
    {
      id: "socio-economic",
      icon: TrendingUp,
      title: "Socio-economic Development",
      image: "img/respect2.jpg",
      description: "By providing training, information and awareness, career advancements, jobs, financial and technical support through implementation of social and economic development programs, sustainable provision of community service and actions in finance, health, educational, social welfare, and self-help for the disadvantaged.",
      highlights: ["Training & Information", "Career Advancement", "Financial Support", "Technical Assistance"],
      color: "from-blue-500 to-cyan-500"
    },
    {
      id: "community-awareness", 
      icon: GraduationCap,
      title: "Community Awareness, Public Health, Education & Capacity Building",
      image: "img/awareness.jpg",
      description: "Kayunga community club develops on a foundation of making sure the community we operate in is healthy, literate and exposed to all information that contributes directly to development. The organization makes sure we develop ways to teach, train, and develop our community through support programs under our areas of focus in socio-economic, education, Public health, sports and any other areas partnering with other community development organizations.",
      highlights: ["Health & Literacy", "Information Access", "Community Training", "Strategic Partnerships"],
      color: "from-green-500 to-emerald-500"
    }
  ];

  const supportAreas = [
    { icon: Briefcase, label: "Finance", color: "from-blue-500 to-cyan-500" },
    { icon: Heart, label: "Health", color: "from-red-500 to-pink-500" },
    { icon: BookOpen, label: "Education", color: "from-green-500 to-emerald-500" },
    { icon: Users, label: "Social Welfare", color: "from-purple-500 to-pink-500" },
    { icon: Shield, label: "Self-Help", color: "from-orange-500 to-amber-500" },
    { icon: Activity, label: "Sports", color: "from-indigo-500 to-blue-500" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-40 pb-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-green-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-purple-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div 
            id="hero-content"
            data-animate
            className={`text-center transform transition-all duration-1000 ${
              isVisible['hero-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Activity className="w-4 h-4 mr-2" />
              Intervention Strategies
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-8">
              <span className="bg-gradient-to-r from-blue-600 via-green-600 to-purple-600 bg-clip-text text-transparent">
                How We Intervene!
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
              Strategic interventions designed for sustainable community development and empowerment
            </p>
          </div>
        </div>
      </div>

      {/* Interventions Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="space-y-20">
            {interventions.map((intervention, index) => {
              const Icon = intervention.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={intervention.id}
                  id={`intervention-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 400} ${
                    isVisible[`intervention-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${!isEven ? 'lg:grid-flow-col-dense' : ''}`}>
                      {/* Image Section */}
                      <div className={`relative h-64 lg:h-auto ${!isEven ? 'lg:col-start-2' : ''}`}>
                        <img
                          src={intervention.image}
                          alt={intervention.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                        
                        {/* Floating Icon */}
                        <div className={`absolute top-6 ${isEven ? 'right-6' : 'left-6'} w-16 h-16 bg-gradient-to-r ${intervention.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className={`p-8 lg:p-12 flex flex-col justify-center ${!isEven ? 'lg:col-start-1' : ''}`}>
                        <div className={`inline-flex items-center bg-gradient-to-r ${intervention.color} bg-opacity-10 px-4 py-2 rounded-full text-sm font-semibold mb-6 w-fit`}>
                          <Icon className="w-4 h-4 mr-2" />
                          Intervention {index + 1}
                        </div>
                        
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 leading-tight">
                          {intervention.title}
                        </h2>
                        
                        <p className="text-lg text-gray-700 leading-relaxed mb-8">
                          {intervention.description}
                        </p>
                        
                        {/* Highlights */}
                        <div className="grid grid-cols-2 gap-3">
                          {intervention.highlights.map((highlight, idx) => (
                            <div key={idx} className="flex items-center">
                              <div className={`w-3 h-3 bg-gradient-to-r ${intervention.color} rounded-full mr-3 flex-shrink-0`}></div>
                              <span className="text-sm font-medium text-gray-700">{highlight}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Support Areas Section */}
      <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="support-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['support-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Areas of <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Focus</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive support across multiple sectors for holistic community development
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-green-500 rounded-full mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {supportAreas.map((area, index) => {
              const Icon = area.icon;
              return (
                <div
                  key={index}
                  id={`area-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 150} ${
                    isVisible[`area-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center">
                    <div className={`w-16 h-16 bg-gradient-to-r ${area.color} rounded-2xl flex items-center justify-center mb-4 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">{area.label}</h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Partnership Section */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div 
            id="partnership"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['partnership'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-3xl p-1 shadow-2xl">
              <div className="bg-white rounded-3xl p-8 md:p-12">
                <Users className="w-16 h-16 text-blue-600 mx-auto mb-6" />
                <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                  Strategic <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">Partnerships</span>
                </h3>
                <p className="text-xl text-gray-700 leading-relaxed">
                  We develop strategic ways to teach, train, and develop our community through 
                  support programs, partnering with other community development organizations 
                  to maximize impact and create sustainable change across all our focus areas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Intervene;